import { useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import {
  popupActionsCol,
  popupBtnPrimary,
  popupBtnSecondary,
} from "@/lib/popupButtons";
import { openTelegramUserLink } from "@/lib/openExternal";
import { useCheckSubscriptionQuest } from "@/api/hooks";
import type { SubscriptionQuestItemOut } from "@/api/types";

/**
 * Попап підписочного квесту (owner 2026-07-16): аватарка каналу/чату +
 * назва + кнопка «Підписатися» (відкриває канал у Telegram) + «Перевірити
 * підписку». Центрований, rounded-3xl — 1:1 зі стилем інших попапів апки.
 * При успіху — анімований 🎉 + «+50₴ нараховано».
 */
export function SubscriptionQuestModal({
  item,
  onClose,
}: {
  item: SubscriptionQuestItemOut;
  onClose: () => void;
}) {
  const check = useCheckSubscriptionQuest();
  // Локальний стан результату (щоб не міняти item з батька миттєво).
  const [done, setDone] = useState(item.is_done);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const onSubscribe = () => {
    openTelegramUserLink(`https://t.me/${item.username}`);
  };

  const onCheck = () => {
    setErrMsg(null);
    check.mutate(item.key, {
      onSuccess: (data) => {
        if (data.status === "credited" || data.status === "already") {
          setDone(true);
        } else {
          setErrMsg(
            data.message ||
              "Ще не бачу підписки 🤔 Підпишись і натисни ще раз.",
          );
        }
      },
      onError: () => {
        setErrMsg("Технічна помилка — спробуй ще раз пізніше.");
      },
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />

        {/* Аватарка каналу/чату */}
        <div className="flex justify-center mb-3">
          <img
            src={item.avatar_url}
            alt={item.title}
            className="h-[4.5rem] w-[4.5rem] rounded-2xl object-cover ring-1 ring-black/5"
            draggable={false}
          />
        </div>

        <div className="text-center mb-4">
          <div className="font-display text-[1.125rem] font-bold text-ink leading-tight mb-1">
            {item.title}
          </div>
          {item.caption && (
            <div className="text-[0.8125rem] text-ink-dim leading-relaxed">
              {item.caption}
            </div>
          )}
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 font-display text-[0.8125rem] font-bold text-primary">
            +{item.reward_uah} ₴
          </div>
        </div>

        {done ? (
          // Успіх / вже виконано
          <div className="text-center">
            <div className="mb-2 flex justify-center">
              <TgEmoji char="🎉" size={44} />
            </div>
            <div className="text-[0.84375rem] font-semibold text-primary mb-3">
              Бонус нараховано ✅
            </div>
            <div className={popupActionsCol}>
            </div>
          </div>
        ) : (
          <>
            <div className={popupActionsCol}>
              <button
                type="button"
                onClick={onSubscribe}
                className={popupBtnPrimary}
              >
                {item.kind === "chat"
                  ? "Підписатися на групу"
                  : "Підписатися на канал"}
              </button>
              <button
                type="button"
                onClick={onCheck}
                disabled={check.isPending}
                className={popupBtnSecondary}
              >
                {check.isPending ? "Перевіряю…" : "Перевірити підписку"}
              </button>
            </div>
            {errMsg && (
              <div className="mt-3 text-center text-[0.78125rem] text-ink-dim leading-relaxed">
                {errMsg}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
